import React, { useContext, useMemo } from 'react';
import { useIntl } from 'react-intl';
import Button from '@material-ui/core/Button';
import makeStyles from '@material-ui/core/styles/makeStyles';
import GetAppIcon from '@material-ui/icons/GetApp';
import {
  FilesContext,
  TableFieldCellProps,
  TableFieldCells,
  TableFieldColumn,
  Tooltip,
  useStaticListSource,
} from '@eas/common-web';
import { DictionaryAutocomplete, DocumentAttachment } from '@models';
import { DocumentOriginator, EssDocumentState } from '@enums';

const useStyles = makeStyles(() => ({
  downloadButton: {
    minWidth: '0',
    boxShadow: '0 0 0',
    padding: 0,
    height: 20,
    '& svg': {
      width: 18,
      height: 18,
    },
    '& p': {
      // contradict the default 'none'
      display: 'block',
    },
  },
}));

export function useCoverLetterColumns({
  onDownloadAttachments,
  downloadAttachmentsDisabled,
}: {
  onDownloadAttachments: () => void;
  downloadAttachmentsDisabled: boolean;
}): TableFieldColumn<DocumentAttachment>[] {
  const intl = useIntl();
  const classes = useStyles();

  const { getFileUrl } = useContext(FilesContext);

  const CoverLetterTypeCell = TableFieldCells.useSelectCellFactory(
    useAttachmentTypeSource
  );

  const OriginatorCell =
    TableFieldCells.useSelectCellFactory(useOriginatorSource);

  const columns: TableFieldColumn<DocumentAttachment>[] = useMemo(
    () => [
      {
        datakey: 'download',
        name: (
          <Tooltip
            placement="top"
            type="HOVER"
            title={intl.formatMessage({
              id: 'ES__ES_FILES__COVER_LETTER__DOWNLOAD_ATTACHMENTS_ZIP',
              defaultMessage: 'Stáhnout přílohy jako ZIP',
            })}
          >
            <span style={{ display: 'inline-flex' }}>
              <Button
                size="small"
                classes={{ root: classes.downloadButton }}
                variant="text"
                disabled={downloadAttachmentsDisabled}
                onClick={(event) => {
                  event.stopPropagation();
                  onDownloadAttachments();
                }}
              >
                <GetAppIcon />
              </Button>
            </span>
          </Tooltip>
        ),
        width: 42,
        CellComponent: function Cell(
          props: TableFieldCellProps<DocumentAttachment>
        ) {
          const disabled =
            !props.rowValue?.file?.id ||
            props.rowValue?.document?.essDocument?.state ===
              EssDocumentState.SHREDDED;

          return (
            <Tooltip
              placement="top"
              type="HOVER"
              title={intl.formatMessage({
                id: 'ES__ES_FILES__COVER_LETTER__DOWNLOAD_ATTACHMENT',
                defaultMessage: 'Stáhnout',
              })}
            >
              <span style={{ display: 'inline-flex' }}>
                <Button
                  download={true}
                  size="small"
                  classes={{ root: classes.downloadButton }}
                  variant="text"
                  onClick={(e: any) => {
                    e.stopPropagation();
                  }}
                  disabled={disabled}
                  href={getFileUrl(props.rowValue?.file?.id)}
                >
                  <GetAppIcon />
                </Button>
              </span>
            </Tooltip>
          );
        },
      },
      {
        datakey: 'label',
        name: intl.formatMessage({
          id: 'ES__DOCUMENT_DELIVERED__DETAIL__ATTACHMENT_COLUMN__LABEL',
          defaultMessage: 'Označení',
        }),
        width: 100,
        CellComponent: TableFieldCells.TextCell,
      },
      {
        datakey: 'name',
        name: intl.formatMessage({
          id: 'ES__DOCUMENT_DELIVERED__DETAIL__ATTACHMENT_COLUMN__TYPE',
          defaultMessage: 'Název přílohy',
        }),
        width: 250,
        CellComponent: TableFieldCells.TextCell,
      },
      {
        datakey: 'file.name',
        name: intl.formatMessage({
          id: 'ES__DOCUMENT_DELIVERED__DETAIL__ATTACHMENT_COLUMN__FILE',
          defaultMessage: 'Soubor',
        }),
        width: 250,
        CellComponent: TableFieldCells.TextCell,
      },
      {
        datakey: 'created',
        name: intl.formatMessage({
          id: 'ES__DOCUMENT_DELIVERED__DETAIL__ATTACHMENT_COLUMN__CREATED',
          defaultMessage: 'Datum vytvoření',
        }),
        width: 250,
        CellComponent: TableFieldCells.DateTimeCell,
      },
      {
        datakey: 'type',
        name: intl.formatMessage({
          id: 'ES__DOCUMENT_DELIVERED__DETAIL__ATTACHMENT_COLUMN__TYPE',
          defaultMessage: 'Typ přílohy',
        }),
        width: 250,
        CellComponent: CoverLetterTypeCell as any,
      },
      {
        datakey: 'document.originator',
        name: intl.formatMessage({
          id: 'ES__DOCUMENT_DELIVERED__DETAIL__ATTACHMENT_COLUMN__DOCUMENT_ORIGINATOR',
          defaultMessage: 'Původce',
        }),
        width: 250,
        CellComponent: OriginatorCell,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [intl]
  );

  return columns;
}

const useAttachmentTypeSource = () =>
  useStaticListSource<DictionaryAutocomplete>([
    { id: 'MAIN', name: 'Hlavní příloha' },
    { id: 'POWER_OF_ATTORNEY', name: 'Plná moc' },
    {
      id: 'CHALLENGED_DECISION',
      name: 'Přílohová obálka',
    },
    {
      id: 'OTHER',
      name: 'Další přílohy',
    },
    {
      id: 'OUTGOING',
      name: 'Přílohy odchozího dokumentu',
    },
    {
      id: 'ENVELOPE',
      name: 'Obálky',
    },
    {
      id: 'DELIVERY_NOTE',
      name: 'Doručenky',
    },
    { id: 'LUSTRUM', name: 'Lustrum' },
    { id: 'INTERNAL', name: 'Interní příloha' },
  ]);
const useOriginatorSource = () =>
  useStaticListSource<DictionaryAutocomplete>([
    { id: DocumentOriginator.OPPONENT, name: 'Odpůrce' },
    { id: DocumentOriginator.PROPONENT, name: 'Navrhovatel' },
    { id: DocumentOriginator.OTHER, name: 'Ostatní' },
  ]);
